class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (typeof window.arkBindCursorFillButtons === 'function') {
      window.arkBindCursorFillButtons(this);
    }
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.releaseBodyScrollLock();
  }

  getDrawerTransitionMs() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--duration-default').trim();
    const ms = parseInt(raw, 10);
    return Number.isFinite(ms) ? ms : 200;
  }

  releaseBodyScrollLock() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    const duration = this.getDrawerTransitionMs();
    let released = false;

    const unlock = () => {
      if (released) return;
      released = true;
      document.body.classList.remove('overflow-hidden');
      overlay?.removeEventListener('transitionend', onOverlayTransitionEnd);
    };

    const onOverlayTransitionEnd = (event) => {
      if (event.target !== overlay || event.propertyName !== 'opacity') return;
      unlock();
    };

    if (overlay) {
      overlay.addEventListener('transitionend', onOverlayTransitionEnd);
    }
    setTimeout(unlock, duration + 50);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (typeof parsedState.item_count === 'number' && parsedState.item_count > 0) {
      this.classList.remove('is-empty');
      this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    const sectionsPayload = parsedState.sections || {};

    this.getSectionsToRender().forEach((section) => {
      const raw = sectionsPayload[section.id];

      // Shopify: "Sections that fail to render are returned as null"
      if (raw == null || raw === '') {
        return;
      }

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const innerSel = section.selector || '.shopify-section';
      let toInject = '';

      try {
        toInject = this.getSectionInnerHTML(raw, innerSel);
      } catch (err) {
        console.error('[cart-drawer] section parse failed', section.id, err);
        return;
      }

      if (toInject !== '') {
        sectionElement.innerHTML = toInject;
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (typeof window.arkBindCursorFillButtons === 'function') {
        window.arkBindCursorFillButtons(this);
      }
      this.open();
    });
  }

  /**
   * Fallback when bundled `sections` in /cart/add.js are missing or all null.
   * Same pattern as cart.js CartItems.onCartUpdate for cart-drawer-items.
   */
  refreshContentsFromCartPage() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.ark-cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
        this.classList.remove('is-empty');
        this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
      })
      .then(() => {
        if (typeof window.arkBindCursorFillButtons === 'function') {
          window.arkBindCursorFillButtons(this);
        }
      })
      .then(() => this.refreshCartIconBubble())
      .catch((e) => {
        console.error('[cart-drawer] refreshContentsFromCartPage', e);
      });
  }

  refreshCartIconBubble() {
    try {
      const root = typeof window.Shopify?.routes?.root === 'string' ? window.Shopify.routes.root : '/';
      const url = new URL(root || '/', window.location.origin);
      url.searchParams.set('section_id', 'cart-icon-bubble');
      return fetch(url.toString())
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const nextBubble = html.getElementById('cart-icon-bubble');
          const cur = document.getElementById('cart-icon-bubble');
          if (nextBubble && cur) {
            cur.innerHTML = nextBubble.innerHTML;
          }
        })
        .catch(() => {});
    } catch (e) {
      return Promise.resolve();
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (html == null || html === '') {
      return '';
    }

    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const el = doc.querySelector(selector);
    if (!el) {
      return '';
    }
    return el.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
